/**
 * Drill-down uses a dedicated virtualized table (option A). Reusing dashboard ReportView +
 * PbsDataViewService would require an adapter that emits master-data-shaped rows plus a synthetic
 * measure column and modal-specific chrome; not a drop-in.
 */
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useVirtualizer } from '@tanstack/react-virtual';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataFieldWithDataType,
  getKeyFromDataFieldWithDataType,
} from '../../../../common-types';
import { Modal } from '../../../common/components/utils/Modal';
import { CONFIDENTIAL_DISPLAY_KEY } from '../../../common/constants/constants';
import type { Months as LegacyMonths } from '../../../common/date-manager/date-manager-constants';
import type { Employee } from '../../../common/v2/api/employee/types';
import { BetterLoadingOverlay } from '../../../common/v2/common/components/BetterLoading';
import type {
  Months,
  TimeSliderState,
} from '../../../common/v2/common/components/timeslider/types';
import { useFieldLabelFormatter } from '../../../common/v2/common/hooks';
import {
  useAllUsedFieldsContext,
  useDomainContext,
  useEmployeeServiceContext,
  useExecutorRoleViewAuthorizationServiceContext,
  useLatestEmployeeVersionContext,
  useLegacyDomainScopedStoresContext,
} from '../../../common/v2/context/contexts';
import { getDisplayValueForMaybeUndefinedValue } from '../../../common/v2/utils';
import SearchableColumnSelector from '../../reports/ColumnSelectorV2';
import {
  employeeToEnrichmentEntry,
  getColumnLayout,
  getDrillDownCandidateFields,
  PINNED_FIELD,
  resolveDrillDownColumns,
  ROW_NUMBER_COLUMN_MIN_WIDTH,
  VALUE_COLUMN_MIN_WIDTH,
} from './drilldownColumns';
import { PbsEmployeeIdFilterPopover } from './PbsEmployeeIdFilterPopover';
import { PbsValueFilterPopover } from './PbsValueFilterPopover';
import { getEmployeesForPbsDrillDown } from './services/getEmployeesForPbsDrillDown';
import { sortPbsDrillDownEmployeeIds } from './services/pbsDrillDownSortUtils';
import {
  DEFAULT_VALUE_FILTER_OPERATION,
  filterEmployeeIdsByValue,
  isValueFilterActive,
  type PbsDrillDownValueFilter,
} from './services/pbsDrillDownValueFilter';
import type {
  DrillDownColumn,
  EnrichmentCacheEntry,
  PbsCellDrillDownContext,
  PbsDrillDownSortField,
  PbsDrillDownSortOrder,
  PbsTableHeaderObj,
  PbsTimeHeaderData,
} from './types';
import { useDataExplorerColumns } from './useDataExplorerColumns';
import type { CsvMetricOpts } from './useExportCsv';
import { useExportCsv } from './useExportCsv';
import { usePbsCellDrillDownModalTour } from './usePbsCellDrillDownModalTour';

const VIEWPORT_ENRICH_BATCH_SIZE = 150;
const ENRICH_DEBOUNCE_MS = 180;
const ROW_HEIGHT = 40;
const TABLE_BODY_HEIGHT = 400;
const OVERSCAN = 20;
const PAGE_SIZE = 100;

const INACTIVE_VALUE_FILTER: PbsDrillDownValueFilter = {
  operation: DEFAULT_VALUE_FILTER_OPERATION,
  threshold: null,
  tenureEqual: null,
};

// Left offsets for the frozen columns, stacked in render order: No. → Employee ID → Value.
// Fixed widths only — a pinned column that could flex-grow would make these offsets wrong.
const EMPLOYEE_ID_COLUMN_MIN_WIDTH = getColumnLayout(PINNED_FIELD).minWidth;
const EMPLOYEE_ID_STICKY_LEFT = ROW_NUMBER_COLUMN_MIN_WIDTH;
const VALUE_STICKY_LEFT =
  ROW_NUMBER_COLUMN_MIN_WIDTH + EMPLOYEE_ID_COLUMN_MIN_WIDTH;
// Hairline boundary between the frozen columns and the horizontally scrollable ones.
const STICKY_BOUNDARY_BORDER = '1px solid rgba(0, 0, 0, 0.12)';

export interface PbsCellDrillDownModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  employeeIds: string[];
  error: string | null;
  /** Time context from the PBS time slider; when provided, employee list query filters by version/term date so name and job title are resolved for that point in time. */
  timeSliderState?: TimeSliderState | null;
  firstMonthOfYear?: Months | null;
  endDate?: string | null;
  /** Context for the current drill-down (measure, segment, date) used for title and prev/next. */
  drillDownContext?: PbsCellDrillDownContext | null;
  /** Human-readable measure label for the modal header. */
  measureLabel?: string;
  /** Time header labels for prev/next date navigation when headerType is 'time'. */
  timeHeaderObjs?: PbsTableHeaderObj[];
  /** Time header data (label + dateRange) for the current metric; used for prev/next dateRange. */
  timeHeadersData?: PbsTimeHeaderData[];
  /** Load employee list for a different date (prev/next). */
  onLoadForContext?: (context: PbsCellDrillDownContext) => void;
  /** Per-employee measure values for the drilled PBS cell (from the same query as the list). */
  metricValueByEmployeeId?: Record<string, number>;
  /** Whether the user may export the Data View; mirrors canExportDataView gating. */
  canExport?: boolean;
}

/**
 * Chrome only: the border and rounded corners. The scrolling happens in TableScroll below,
 * which is also what TableHeader sticks to.
 */
const TableContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'clip',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 4,
}));

/**
 * The single scroll container for both axes. Header and body live inside it so that they
 * scroll sideways together — a header outside it could never stay aligned with its column.
 */
const TableScroll = styled('div')(({ theme }) => ({
  overflow: 'auto',
  minHeight: 0,
  backgroundColor: theme.palette.background.paper,
}));

/**
 * Shared width for header and every body row. minWidth is set in px from the column layout
 * (see tableMinWidth); width:100% fills the scrollport when columns are few. Do NOT use
 * width:max-content here — that lets each row size to its longest cell text, so one wide
 * value (e.g. a long org name) shifts that row and breaks column alignment.
 */
const rowWidthCss = {
  width: '100%',
  boxSizing: 'border-box' as const,
} as const;

const TableHeader = styled('div')(({ theme }) => ({
  ...rowWidthCss,
  display: 'flex',
  alignItems: 'center',
  minHeight: ROW_HEIGHT,
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: '#f5f5f5',
  fontSize: theme.typography.pxToRem(12),
  color: 'rgba(0, 0, 0, 0.6)',
  position: 'sticky',
  top: 0,
  zIndex: 3,
}));

/**
 * `flex: 1 0 <minWidth>` — grow into spare width, never shrink below minWidth. The old
 * `flex: 1` let 12 columns squeeze into the modal until every header read "Start …".
 * box-sizing + overflow keep long cell text from expanding the row past the shared width.
 */
const cellSizingCss = (minWidth: number) => ({
  flex: `1 0 ${minWidth}px`,
  minWidth,
  boxSizing: 'border-box' as const,
  padding: '0 12px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
});

/**
 * Sticky-left chrome for the frozen No. / Employee ID / Value columns. `background:
 * 'inherit'` picks up the row's stripe/hover colour so the column stays opaque as other
 * columns scroll underneath it — a transparent pinned cell would let them bleed through.
 */
const stickyLeftCss = (left: number) => ({
  position: 'sticky' as const,
  left,
  zIndex: 2,
  background: 'inherit',
});

const HeaderCell = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== '$minWidth' && prop !== '$noGrow' && prop !== '$stickyLeft',
})<{ $minWidth: number; $noGrow?: boolean; $stickyLeft?: number }>(
  ({ $minWidth, $noGrow, $stickyLeft }) => ({
    ...cellSizingCss($minWidth),
    ...($noGrow ? { flex: `0 0 ${$minWidth}px`, maxWidth: $minWidth } : null),
    ...($stickyLeft != null ? stickyLeftCss($stickyLeft) : null),
    letterSpacing: 0.02,
  })
);

const Row = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isEven',
})<{ $isEven: boolean }>(({ theme, $isEven }) => ({
  ...rowWidthCss,
  display: 'flex',
  alignItems: 'center',
  height: ROW_HEIGHT,
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: $isEven ? '#fafafa' : '#fff',
  fontSize: theme.typography.pxToRem(13),
  boxSizing: 'border-box',
  color: 'rgba(0, 0, 0, 0.87)',
  '&:hover': {
    backgroundColor: '#f0f0f0',
  },
}));

const Cell = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== '$minWidth' && prop !== '$noGrow' && prop !== '$stickyLeft',
})<{ $minWidth: number; $noGrow?: boolean; $stickyLeft?: number }>(
  ({ $minWidth, $noGrow, $stickyLeft }) => ({
    ...cellSizingCss($minWidth),
    ...($noGrow ? { flex: `0 0 ${$minWidth}px`, maxWidth: $minWidth } : null),
    ...($stickyLeft != null ? stickyLeftCss($stickyLeft) : null),
    color: 'rgba(0, 0, 0, 0.87)',
  })
);

/** Shown instead of blank so an empty value is legible as "no data", not a broken cell. */
const EmptyValue = styled('span')({
  color: 'rgba(0, 0, 0, 0.32)',
});

const Footer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  borderTop: `1px solid ${theme.palette.divider}`,
  flexShrink: 0,
  backgroundColor: theme.palette.background.paper,
}));

const TopBar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: '#f5f5f5',
  flexShrink: 0,
}));

const DateNav = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

/** Text link style — matches Prev/Next date controls without a filled button chrome. */
const TourLinkButton = styled(Button)(({ theme }) => ({
  flexShrink: 0,
  minWidth: 'auto',
  padding: theme.spacing(0.25, 0.75),
  color: theme.palette.primary.main,
  fontWeight: 500,
  fontSize: theme.typography.pxToRem(13),
  lineHeight: 1.43,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  backgroundColor: 'transparent',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    boxShadow: 'none',
  },
  '& .MuiButton-endIcon': {
    marginLeft: theme.spacing(0.75),
    marginRight: 0,
  },
}));

const ModalContentWrap = styled('div')<{ $minHeight: number }>(
  ({ $minHeight }) => ({
    position: 'relative',
    minHeight: $minHeight,
    display: 'flex',
    flexDirection: 'column',
  })
);

const CellLoaderWrap = styled('span')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  // Cap to the cell so spinner + long text cannot inflate the row's max-content width.
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
});

const CellText = styled('span')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
});

// Opaque approximations of DESIGN.md's Teal Tint (#26A69A1A) over the canvas — flat, not
// alpha, because this column is pinned: a translucent fill would let scrolled columns
// bleed through underneath it. No side border or bold weight (DESIGN.md: teal is the only
// chrome color and is never a decorative stripe); the tint alone marks it as the metric.
const ValueHeaderCell = styled(HeaderCell)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#173430' : '#e1f1ef',
}));

const ValueBodyCell = styled(Cell)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#122b27' : '#edf8f6',
  fontVariantNumeric: 'tabular-nums',
}));

const SortHeaderButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  maxWidth: '100%',
  margin: 0,
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  font: 'inherit',
  color: 'inherit',
  textAlign: 'left',
  '&:hover': {
    color: theme.palette.primary.main,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const OverflowTooltip = ({
  title,
  children,
}: {
  title?: string;
  children: React.ReactElement<any>;
}) => {
  const ref = useRef<HTMLElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setIsOverflowing(el.scrollWidth > el.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [title]);

  if (!title) return children;

  return (
    <Tooltip
      title={title}
      disableHoverListener={!isOverflowing}
      disableFocusListener={!isOverflowing}
      disableTouchListener={!isOverflowing}
    >
      {React.cloneElement(children, { ref })}
    </Tooltip>
  );
};

/**
 * A draggable column header. No., Employee ID and Value are rendered outside the
 * sortable context, so only employee-attribute columns can be dragged.
 */
const SortableHeaderCell = ({
  column,
  label,
}: {
  column: DrillDownColumn;
  label: string;
}) => {
  const { t } = useTranslation();
  const id = getKeyFromDataFieldWithDataType(column.field);
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  return (
    <Tooltip
      title={
        column.accessible
          ? label
          : `${label} — ${t('pages:reports.columnSelector.searchableView.noAccess')}`
      }
    >
      <HeaderCell
        ref={setNodeRef}
        $minWidth={getColumnLayout(column.field).minWidth}
        {...attributes}
        {...listeners}
        // After the spread: dnd-kit sets role="button", but this is a table header.
        role="columnheader"
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        {/* Static (not just on hover) so the table signals "draggable" on first glance. */}
        <DragIndicatorIcon
          aria-hidden
          sx={{ fontSize: 16, color: 'rgba(0, 0, 0, 0.3)', flexShrink: 0 }}
        />
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
        {!column.accessible && (
          <LockOutlinedIcon
            sx={{ fontSize: 14, opacity: 0.6, flexShrink: 0 }}
          />
        )}
      </HeaderCell>
    </Tooltip>
  );
};

export const PbsCellDrillDownModal: React.FC<PbsCellDrillDownModalProps> = ({
  open,
  onClose,
  loading,
  employeeIds,
  error,
  timeSliderState,
  firstMonthOfYear,
  endDate,
  drillDownContext,
  measureLabel,
  timeHeaderObjs = [],
  timeHeadersData = [],
  onLoadForContext,
  metricValueByEmployeeId = {},
  canExport = true,
}) => {
  const { t, i18n } = useTranslation();
  const employeeService = useEmployeeServiceContext();
  const latestEmployeeVersion = useLatestEmployeeVersionContext();
  const legacyDomainScopedStores = useLegacyDomainScopedStoresContext();
  const { cachedMasterDataApiService, metricStore } = legacyDomainScopedStores;

  const domain = useDomainContext();
  const allUsedFields = useAllUsedFieldsContext();
  const authorizationService = useExecutorRoleViewAuthorizationServiceContext();
  const formatFieldLabel = useFieldLabelFormatter();
  const { fields: savedFields, saveFields } = useDataExplorerColumns();
  const [isColumnSelectorOpen, setColumnSelectorOpen] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor));

  const useDataViewEnrichment =
    !!endDate &&
    !!latestEmployeeVersion &&
    !!firstMonthOfYear &&
    !!cachedMasterDataApiService;

  /** Every employee field this domain could offer, permissions aside. */
  const candidateFields = useMemo(
    () => getDrillDownCandidateFields(domain, allUsedFields),
    [domain, allUsedFields]
  );

  /**
   * The saved columns, each tagged with whether the role may still read it. Fields whose
   * access was revoked keep their column but are rendered as CONFIDENTIAL and kept out of
   * the query below.
   */
  const columns = useMemo(
    () =>
      resolveDrillDownColumns(
        savedFields,
        candidateFields,
        domain,
        authorizationService
      ),
    [savedFields, candidateFields, domain, authorizationService]
  );

  const accessibleFields = useMemo(
    () => columns.filter((c) => c.accessible).map((c) => c.field),
    [columns]
  );

  const disabledFields = useMemo(
    () => candidateFields.filter((f) => !authorizationService.canSeeField(f)),
    [candidateFields, authorizationService]
  );

  const columnIds = useMemo(
    () => columns.map((c) => getKeyFromDataFieldWithDataType(c.field)),
    [columns]
  );

  const handleApplyFields = useCallback(
    (fields: DataFieldWithDataType[]) => {
      saveFields(fields);
      setColumnSelectorOpen(false);
    },
    [saveFields]
  );

  const handleHeaderDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = columnIds.indexOf(String(active.id));
      const newIndex = columnIds.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;
      saveFields(arrayMove(columns, oldIndex, newIndex).map((c) => c.field));
    },
    [columnIds, columns, saveFields]
  );

  const [enrichmentCache, setEnrichmentCache] = useState<
    Record<string, EnrichmentCacheEntry>
  >({});
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

  const inFlightIdsRef = useRef<Set<string>>(new Set());
  const enrichBatchInFlightRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const enrichmentCacheRef = useRef<Record<string, EnrichmentCacheEntry>>({});
  const enrichTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [loadedCount, setLoadedCount] = useState(PAGE_SIZE);
  const [sortField, setSortField] =
    useState<PbsDrillDownSortField>('employeeId');
  const [sortOrder, setSortOrder] = useState<PbsDrillDownSortOrder>('asc');
  const [employeeIdFilter, setEmployeeIdFilter] = useState('');
  const [valueFilter, setValueFilter] = useState<PbsDrillDownValueFilter>(
    INACTIVE_VALUE_FILTER
  );

  useEffect(() => {
    if (open) {
      setEnrichmentCache({});
      setLoadedCount(PAGE_SIZE);
      setSortField('employeeId');
      setSortOrder('asc');
      setEmployeeIdFilter('');
      setValueFilter(INACTIVE_VALUE_FILTER);
      inFlightIdsRef.current = new Set();
      enrichBatchInFlightRef.current = false;
      enrichmentCacheRef.current = {};
    }
  }, [open, employeeIds]);

  // Sorted: this identifies the SET of queried fields, not their order. Reordering columns
  // does not change which values were fetched, so it must not invalidate the cache — keying
  // on the display order would blank the table and re-enrich on every drag.
  const accessibleFieldsKey = useMemo(
    () =>
      accessibleFields.map(getKeyFromDataFieldWithDataType).sort().join('|'),
    [accessibleFields]
  );

  // Selecting a new column changes the query, so already-cached rows are missing its value.
  // Drop the cache and let the viewport re-enrich, or the new column would stay blank.
  useEffect(() => {
    setEnrichmentCache({});
    enrichmentCacheRef.current = {};
    inFlightIdsRef.current = new Set();
    enrichBatchInFlightRef.current = false;
  }, [accessibleFieldsKey]);

  const searchQueryNormalized = useMemo(
    () => employeeIdFilter.trim().toLowerCase(),
    [employeeIdFilter]
  );

  const showMeasureColumn =
    measureLabel != null &&
    measureLabel.length > 0 &&
    Object.keys(metricValueByEmployeeId).length > 0;

  // Shown in the Value column header and its filter popover instead of the generic
  // "Value" label, so Data Explorer names the metric it is actually displaying.
  const valueColumnLabel =
    measureLabel ??
    t('pages:dashboards.peopleBalanceSheet.cellDrillDown.valueColumnHeader', {
      defaultValue: 'Value',
    });

  const appliedValueFilter: PbsDrillDownValueFilter = showMeasureColumn
    ? valueFilter
    : INACTIVE_VALUE_FILTER;

  const isListFiltered =
    searchQueryNormalized.length > 0 || isValueFilterActive(appliedValueFilter);

  const handleResetFilters = useCallback(() => {
    setEmployeeIdFilter('');
    setValueFilter(INACTIVE_VALUE_FILTER);
    setLoadedCount(PAGE_SIZE);
  }, []);

  useEffect(() => {
    setLoadedCount(PAGE_SIZE);
  }, [searchQueryNormalized, valueFilter]);

  /**
   * Client-side only. Employee-ID substring search, then optional Value comparison
   * (metric column only). Neither path reloads from the server.
   */
  const filteredEmployeeIds = useMemo(() => {
    const bySearch = searchQueryNormalized
      ? employeeIds.filter((id) =>
          id.toLowerCase().includes(searchQueryNormalized)
        )
      : employeeIds;
    return filterEmployeeIdsByValue(
      bySearch,
      metricValueByEmployeeId,
      appliedValueFilter
    );
  }, [
    employeeIds,
    searchQueryNormalized,
    metricValueByEmployeeId,
    appliedValueFilter,
  ]);

  const sortedEmployeeIds = useMemo(
    () =>
      sortPbsDrillDownEmployeeIds(
        filteredEmployeeIds,
        metricValueByEmployeeId,
        sortField,
        sortOrder
      ),
    [filteredEmployeeIds, metricValueByEmployeeId, sortField, sortOrder]
  );

  const paginatedIds = useMemo(
    () => sortedEmployeeIds.slice(0, loadedCount),
    [sortedEmployeeIds, loadedCount]
  );
  const hasMore = loadedCount < sortedEmployeeIds.length;

  // Only the Value column is sortable; first click sorts asc, subsequent clicks toggle.
  const sortFieldRef = useRef(sortField);
  sortFieldRef.current = sortField;
  const handleSortRequest = useCallback(() => {
    if (sortFieldRef.current === 'metricValue') {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField('metricValue');
      setSortOrder('asc');
    }
  }, []);

  // The "No." header: not sortable, not draggable, frozen at the left edge.
  const renderLeadingHeader = useCallback(
    (label: string, minWidth: number, options?: { noGrow?: boolean }) => (
      <HeaderCell
        $minWidth={minWidth}
        $noGrow={options?.noGrow}
        $stickyLeft={0}
        role="columnheader"
      >
        {label}
      </HeaderCell>
    ),
    []
  );

  const virtualizer = useVirtualizer({
    count: paginatedIds.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN,
  });

  const rangeStart = virtualizer.range?.startIndex ?? -1;
  const rangeEnd = virtualizer.range?.endIndex ?? -1;

  const { startTour: startModalTour } = usePbsCellDrillDownModalTour({
    open,
    loading,
    error,
    employeeCount: employeeIds.length,
    tableInDom: sortedEmployeeIds.length > 0,
  });

  const canShowTour = !loading && error == null && employeeIds.length > 0;

  const formatMetricForDrillDown = useCallback(
    (value: number) => {
      if (drillDownContext == null) {
        return new Intl.NumberFormat(i18n.language, {
          maximumFractionDigits: 4,
        }).format(value);
      }
      return metricStore.formatMetricValue(
        value,
        drillDownContext.selectedMeasure
      );
    },
    [metricStore, drillDownContext, i18n.language]
  );

  const valueFormatType = useMemo(() => {
    if (drillDownContext == null) return undefined;
    try {
      return metricStore.getMetricDetailsFromMetricId(
        drillDownContext.selectedMeasure
      ).formatType;
    } catch {
      return undefined;
    }
  }, [metricStore, drillDownContext]);

  const enrichIds = useCallback(
    (idsToEnrich: string[]) => {
      if (idsToEnrich.length === 0) return;
      const inFlight = inFlightIdsRef.current;
      const toFetch = idsToEnrich
        .filter((id) => !inFlight.has(id) && !enrichmentCacheRef.current[id])
        .slice(0, VIEWPORT_ENRICH_BATCH_SIZE);
      if (toFetch.length === 0) return;
      if (enrichBatchInFlightRef.current) return;
      enrichBatchInFlightRef.current = true;
      toFetch.forEach((id) => inFlight.add(id));
      setLoadingIds((prev) => new Set([...prev, ...toFetch]));

      const onResult = (next: Record<string, EnrichmentCacheEntry>) => {
        toFetch.forEach((id) => inFlight.delete(id));
        enrichBatchInFlightRef.current = false;
        const merged = { ...enrichmentCacheRef.current, ...next };
        enrichmentCacheRef.current = merged;
        setLoadingIds((prev) => {
          const nextSet = new Set(prev);
          toFetch.forEach((id) => nextSet.delete(id));
          return nextSet;
        });
        setEnrichmentCache(merged);
      };

      const onFailure = () => {
        toFetch.forEach((id) => inFlight.delete(id));
        enrichBatchInFlightRef.current = false;
        setLoadingIds((prev) => {
          const nextSet = new Set(prev);
          toFetch.forEach((id) => nextSet.delete(id));
          return nextSet;
        });
      };

      if (
        useDataViewEnrichment &&
        endDate &&
        firstMonthOfYear &&
        latestEmployeeVersion &&
        cachedMasterDataApiService
      ) {
        getEmployeesForPbsDrillDown(
          toFetch,
          endDate,
          firstMonthOfYear as LegacyMonths,
          latestEmployeeVersion,
          cachedMasterDataApiService,
          accessibleFields
        )
          .then((entries) => {
            const next: Record<string, EnrichmentCacheEntry> = {};
            for (const e of entries) {
              next[e.id] = e.values;
            }
            onResult(next);
          })
          .catch(onFailure);
      } else {
        employeeService
          .list(
            toFetch,
            timeSliderState ?? undefined,
            firstMonthOfYear ?? undefined
          )
          .then((employees: Employee[]) => {
            const next: Record<string, EnrichmentCacheEntry> = {};
            for (const e of employees) {
              next[e.id] = employeeToEnrichmentEntry(e);
            }
            onResult(next);
          })
          .catch(onFailure);
      }
    },
    [
      employeeService,
      timeSliderState,
      firstMonthOfYear,
      useDataViewEnrichment,
      endDate,
      latestEmployeeVersion,
      cachedMasterDataApiService,
      accessibleFields,
    ]
  );

  // Enrich only the current virtual window; depend on scroll range (not virtual item
  // array identity) so we do not fan out parallel requests on every render.
  useEffect(() => {
    if (!open || paginatedIds.length === 0 || loading || error) return;
    if (rangeStart < 0 || rangeEnd < 0) return;
    if (enrichTimeoutRef.current) clearTimeout(enrichTimeoutRef.current);
    enrichTimeoutRef.current = setTimeout(() => {
      const start = Math.max(0, rangeStart - OVERSCAN);
      const end = Math.min(paginatedIds.length - 1, rangeEnd + OVERSCAN);
      const visibleIds: string[] = [];
      for (let i = start; i <= end; i++) {
        visibleIds.push(paginatedIds[i]);
      }
      const missingIds = visibleIds.filter(
        (id) =>
          !enrichmentCacheRef.current[id] && !inFlightIdsRef.current.has(id)
      );
      enrichIds(missingIds);
      enrichTimeoutRef.current = null;
    }, ENRICH_DEBOUNCE_MS);
    return () => {
      if (enrichTimeoutRef.current) {
        clearTimeout(enrichTimeoutRef.current);
        enrichTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, paginatedIds, loading, error, rangeStart, rangeEnd, enrichIds]);

  const csvMetricOpts: CsvMetricOpts | undefined = useMemo(
    () =>
      showMeasureColumn && drillDownContext
        ? {
            metricById: metricValueByEmployeeId,
            measureLabel,
            formatMetricValue: formatMetricForDrillDown,
          }
        : showMeasureColumn
          ? {
              metricById: metricValueByEmployeeId,
              measureLabel,
            }
          : undefined,
    [
      showMeasureColumn,
      drillDownContext,
      metricValueByEmployeeId,
      measureLabel,
      formatMetricForDrillDown,
    ]
  );

  const { isExporting, exportProgress, handleExport } = useExportCsv({
    sortedEmployeeIds,
    columns,
    enrichmentCache,
    setEnrichmentCache,
    csvMetricOpts,
    useDataViewEnrichment,
    endDate,
    firstMonthOfYear,
    latestEmployeeVersion,
    cachedMasterDataApiService,
    employeeService,
    timeSliderState,
    cellDateRange: drillDownContext?.dateRange ?? null,
    valueFilter: appliedValueFilter,
  });

  const handleLoadMore = useCallback(() => {
    setLoadedCount((prev) =>
      Math.min(prev + PAGE_SIZE, sortedEmployeeIds.length)
    );
  }, [sortedEmployeeIds.length]);

  // The header now lives inside the scroller, so its row counts towards the height.
  const tableHeight = useMemo(
    () =>
      ROW_HEIGHT +
      Math.min(
        TABLE_BODY_HEIGHT,
        Math.max(paginatedIds.length, 1) * ROW_HEIGHT
      ),
    [paginatedIds.length]
  );

  /**
   * Floor width for header + body. Shared across every row so a long cell value cannot
   * widen one row and desync columns. Spare scrollport width is shared via flex-grow.
   */
  const tableMinWidth = useMemo(() => {
    let total = EMPLOYEE_ID_STICKY_LEFT + EMPLOYEE_ID_COLUMN_MIN_WIDTH;
    if (showMeasureColumn) total += VALUE_COLUMN_MIN_WIDTH;
    for (const column of columns) {
      total += getColumnLayout(column.field).minWidth;
    }
    return total;
  }, [columns, showMeasureColumn]);

  const modalTitle = useMemo(() => {
    const parts: string[] = [];
    if (measureLabel) parts.push(measureLabel);
    if (
      drillDownContext?.segmentLevel === 2 &&
      drillDownContext.segmentLevel1Name
    ) {
      parts.push(
        getDisplayValueForMaybeUndefinedValue(
          drillDownContext.segmentLevel1Name
        )
      );
    }
    if (drillDownContext?.segmentName) {
      parts.push(
        getDisplayValueForMaybeUndefinedValue(drillDownContext.segmentName)
      );
    }
    return parts.length > 0
      ? parts.join(' | ')
      : t('pages:dashboards.peopleBalanceSheet.cellDrillDown.title', {
          defaultValue: 'Underlying employees',
        });
  }, [
    measureLabel,
    drillDownContext?.segmentLevel,
    drillDownContext?.segmentLevel1Name,
    drillDownContext?.segmentName,
    t,
  ]);

  const dateLabel = drillDownContext?.headerValue ?? '';
  const isTimeHeader = drillDownContext?.headerType === 'time';
  const timeHeaderValues = useMemo(
    () => timeHeaderObjs.map((h) => h.value),
    [timeHeaderObjs]
  );
  const currentTimeIndex = dateLabel ? timeHeaderValues.indexOf(dateLabel) : -1;
  const hasPrevDate = isTimeHeader && currentTimeIndex > 0;
  const hasNextDate =
    isTimeHeader &&
    currentTimeIndex >= 0 &&
    currentTimeIndex < timeHeaderValues.length - 1;
  const hasDateNav = isTimeHeader && timeHeaderValues.length > 1;
  const handlePrevDate = useCallback(() => {
    if (!hasPrevDate || !drillDownContext || !onLoadForContext) return;
    const prevValue = timeHeaderValues[currentTimeIndex - 1];
    const dateRange =
      timeHeadersData.find((t) => t.label === prevValue)?.dateRange ??
      drillDownContext.dateRange;
    onLoadForContext({
      ...drillDownContext,
      headerValue: prevValue,
      dateRange,
    });
  }, [
    hasPrevDate,
    drillDownContext,
    onLoadForContext,
    timeHeaderValues,
    currentTimeIndex,
    timeHeadersData,
  ]);
  const handleNextDate = useCallback(() => {
    if (!hasNextDate || !drillDownContext || !onLoadForContext) return;
    const nextValue = timeHeaderValues[currentTimeIndex + 1];
    const dateRange =
      timeHeadersData.find((t) => t.label === nextValue)?.dateRange ??
      drillDownContext.dateRange;
    onLoadForContext({
      ...drillDownContext,
      headerValue: nextValue,
      dateRange,
    });
  }, [
    hasNextDate,
    drillDownContext,
    onLoadForContext,
    timeHeaderValues,
    currentTimeIndex,
    timeHeadersData,
  ]);

  const MIN_CONTENT_HEIGHT = TABLE_BODY_HEIGHT + 200;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={modalTitle}
      titleRightAdornment={
        <>
          <IconButton
            aria-label={t(
              'pages:dashboards.peopleBalanceSheet.cellDrillDown.close',
              { defaultValue: 'Close' }
            )}
            onClick={onClose}
            size="small"
            sx={{ color: 'secondary.main', ml: 0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      }
      // Wide columns sets need the room; cap so it does not span an ultrawide display.
      width="min(1400px, 95vw)"
    >
      <ModalContentWrap $minHeight={MIN_CONTENT_HEIGHT}>
        {isColumnSelectorOpen && (
          <SearchableColumnSelector
            selectedFields={columns.map((c) => c.field)}
            allowedFields={candidateFields}
            disabledFields={disabledFields}
            reorderable
            handleApplyFields={handleApplyFields}
            onClose={() => setColumnSelectorOpen(false)}
          />
        )}
        {loading && (
          <BetterLoadingOverlay
            debugString="PbsCellDrillDown"
            isPending={true}
          />
        )}
        {!loading && error ? (
          <Typography sx={{ p: 2 }} color="error">
            {error}
          </Typography>
        ) : !loading && employeeIds.length === 0 ? (
          <Typography sx={{ p: 2 }}>
            {t(
              'pages:dashboards.peopleBalanceSheet.cellDrillDown.noEmployees',
              { defaultValue: 'No employees' }
            )}
          </Typography>
        ) : (
          <>
            <TopBar
              data-pbs-drilldown-tour="modal-intro"
              style={{ flexWrap: 'wrap', gap: 8 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
                >
                  {isListFiltered ? (
                    <>
                      {t(
                        'pages:dashboards.peopleBalanceSheet.cellDrillDown.showingFiltered',
                        {
                          defaultValue:
                            '{{shown}} of {{total}} employees (filtered)',
                          shown: sortedEmployeeIds.length.toLocaleString(),
                          total: employeeIds.length.toLocaleString(),
                        }
                      )}
                    </>
                  ) : (
                    <>
                      {employeeIds.length.toLocaleString()}{' '}
                      {t(
                        'pages:dashboards.peopleBalanceSheet.cellDrillDown.employeesCount',
                        { defaultValue: 'employees' }
                      )}
                    </>
                  )}
                </Typography>
                {isListFiltered && (
                  <TourLinkButton
                    size="small"
                    variant="text"
                    disableElevation
                    onClick={handleResetFilters}
                  >
                    {t(
                      'pages:dashboards.peopleBalanceSheet.cellDrillDown.resetFilters',
                      { defaultValue: 'Reset filters' }
                    )}
                  </TourLinkButton>
                )}
              </div>
              <DateNav>
                <Tooltip
                  title={
                    useDataViewEnrichment
                      ? ''
                      : t(
                          'pages:dashboards.peopleBalanceSheet.cellDrillDown.columnsUnavailable',
                          {
                            defaultValue:
                              'Column selection is unavailable for this cell.',
                          }
                        )
                  }
                >
                  {/* Tooltip needs a non-disabled node to receive hover events. */}
                  <span data-pbs-drilldown-tour="modal-columns">
                    <TourLinkButton
                      size="small"
                      variant="text"
                      disableElevation
                      disabled={!useDataViewEnrichment}
                      endIcon={
                        <ViewColumnIcon sx={{ fontSize: 16 }} aria-hidden />
                      }
                      onClick={() => setColumnSelectorOpen(true)}
                    >
                      {t(
                        'pages:dashboards.peopleBalanceSheet.cellDrillDown.columns',
                        { defaultValue: 'Columns' }
                      )}
                      {columns.length > 0 && ` (${columns.length})`}
                    </TourLinkButton>
                  </span>
                </Tooltip>
                {(canShowTour || hasDateNav) && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ alignSelf: 'center', height: 20, mx: 0.5 }}
                  />
                )}
                {canShowTour && (
                  <Tooltip
                    title={t(
                      'pages:dashboards.peopleBalanceSheet.cellDrillDown.startTour',
                      { defaultValue: 'Take a quick tour' }
                    )}
                  >
                    <IconButton
                      aria-label={t(
                        'pages:dashboards.peopleBalanceSheet.cellDrillDown.startTour',
                        { defaultValue: 'Take a quick tour' }
                      )}
                      size="small"
                      onClick={startModalTour}
                      sx={{ color: 'primary.main' }}
                    >
                      <HelpOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {canShowTour && hasDateNav && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ alignSelf: 'center', height: 20, mx: 0.5 }}
                  />
                )}
                {hasDateNav && (
                  <>
                    <Button
                      size="small"
                      startIcon={<ChevronLeftIcon />}
                      onClick={handlePrevDate}
                      disabled={!hasPrevDate}
                      aria-label={t(
                        'pages:dashboards.peopleBalanceSheet.cellDrillDown.prevDate',
                        { defaultValue: 'Previous period' }
                      )}
                    >
                      {t(
                        'pages:dashboards.peopleBalanceSheet.cellDrillDown.prev',
                        { defaultValue: 'Prev' }
                      )}
                    </Button>
                    <Typography
                      variant="body2"
                      sx={{
                        minWidth: 80,
                        textAlign: 'center',
                        color: 'rgba(0, 0, 0, 0.6)',
                      }}
                    >
                      {dateLabel}
                    </Typography>
                    <Button
                      size="small"
                      endIcon={<ChevronRightIcon />}
                      onClick={handleNextDate}
                      disabled={!hasNextDate}
                      aria-label={t(
                        'pages:dashboards.peopleBalanceSheet.cellDrillDown.nextDate',
                        { defaultValue: 'Next period' }
                      )}
                    >
                      {t(
                        'pages:dashboards.peopleBalanceSheet.cellDrillDown.next',
                        { defaultValue: 'Next' }
                      )}
                    </Button>
                  </>
                )}
              </DateNav>
            </TopBar>
            {sortedEmployeeIds.length === 0 ? (
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  alignItems: 'flex-start',
                }}
              >
                <Typography>
                  {t(
                    'pages:dashboards.peopleBalanceSheet.cellDrillDown.noFilterMatches',
                    {
                      defaultValue:
                        'No employees match your filters. Try adjusting or clearing them.',
                    }
                  )}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleResetFilters}
                >
                  {t(
                    'pages:dashboards.peopleBalanceSheet.cellDrillDown.resetFilters',
                    { defaultValue: 'Reset filters' }
                  )}
                </Button>
              </Box>
            ) : (
              <TableContainer data-pbs-drilldown-tour="modal-table">
                <TableScroll ref={scrollRef} style={{ height: tableHeight }}>
                  <TableHeader style={{ minWidth: tableMinWidth }}>
                    {/*
                     * No. and Employee ID never appear in the column picker.
                     * Employee ID keeps its curated label (CSV uses the same key).
                     */}
                    {renderLeadingHeader(
                      t(
                        'pages:dashboards.peopleBalanceSheet.cellDrillDown.rowNumber',
                        { defaultValue: 'No.' }
                      ),
                      ROW_NUMBER_COLUMN_MIN_WIDTH,
                      { noGrow: true }
                    )}
                    <HeaderCell
                      $minWidth={EMPLOYEE_ID_COLUMN_MIN_WIDTH}
                      $noGrow
                      $stickyLeft={EMPLOYEE_ID_STICKY_LEFT}
                      role="columnheader"
                      data-pbs-drilldown-tour="modal-search"
                      style={
                        showMeasureColumn
                          ? undefined
                          : { borderRight: STICKY_BOUNDARY_BORDER }
                      }
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.25,
                          width: '100%',
                          minWidth: 0,
                        }}
                      >
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {t(
                            'pages:dashboards.peopleBalanceSheet.cellDrillDown.employeeId',
                            { defaultValue: 'Employee ID' }
                          )}
                        </span>
                        <PbsEmployeeIdFilterPopover
                          value={employeeIdFilter}
                          onChange={setEmployeeIdFilter}
                        />
                      </Box>
                    </HeaderCell>
                    {showMeasureColumn ? (
                      <ValueHeaderCell
                        $minWidth={VALUE_COLUMN_MIN_WIDTH}
                        $noGrow
                        $stickyLeft={VALUE_STICKY_LEFT}
                        role="columnheader"
                        data-pbs-drilldown-tour="modal-value"
                        aria-sort={
                          sortField === 'metricValue'
                            ? sortOrder === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                        }
                        style={{ borderRight: STICKY_BOUNDARY_BORDER }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.25,
                            width: '100%',
                            minWidth: 0,
                          }}
                        >
                          <SortHeaderButton
                            type="button"
                            onClick={handleSortRequest}
                            aria-label={t(
                              'pages:dashboards.peopleBalanceSheet.cellDrillDown.sortByColumn',
                              {
                                defaultValue: 'Sort by {{column}}',
                                column: valueColumnLabel,
                              }
                            )}
                            style={{ flex: 1, minWidth: 0, maxWidth: '100%' }}
                          >
                            <OverflowTooltip title={valueColumnLabel}>
                              <span
                                style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {valueColumnLabel}
                              </span>
                            </OverflowTooltip>
                            {sortField === 'metricValue' ? (
                              sortOrder === 'asc' ? (
                                <ArrowUpwardIcon
                                  sx={{ fontSize: 16 }}
                                  aria-hidden
                                />
                              ) : (
                                <ArrowDownwardIcon
                                  sx={{ fontSize: 16 }}
                                  aria-hidden
                                />
                              )
                            ) : (
                              <UnfoldMoreIcon
                                sx={{ fontSize: 16, opacity: 0.45 }}
                                aria-hidden
                              />
                            )}
                          </SortHeaderButton>
                          <PbsValueFilterPopover
                            formatType={valueFormatType}
                            formatThreshold={formatMetricForDrillDown}
                            value={valueFilter}
                            onChange={setValueFilter}
                            measureLabel={measureLabel}
                          />
                        </Box>
                      </ValueHeaderCell>
                    ) : null}
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      modifiers={[restrictToHorizontalAxis]}
                      onDragEnd={handleHeaderDragEnd}
                    >
                      <SortableContext
                        items={columnIds}
                        strategy={horizontalListSortingStrategy}
                      >
                        {columns.map((column) => (
                          <SortableHeaderCell
                            key={getKeyFromDataFieldWithDataType(column.field)}
                            column={column}
                            label={formatFieldLabel(column.field)}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  </TableHeader>
                  <div
                    style={{
                      height: `${virtualizer.getTotalSize()}px`,
                      width: '100%',
                      minWidth: tableMinWidth,
                      position: 'relative',
                    }}
                  >
                    {virtualizer.getVirtualItems().map((virtualRow) => {
                      const id = paginatedIds[virtualRow.index];
                      const entry = enrichmentCache[id];
                      const isRowLoading = loadingIds.has(id);
                      return (
                        <Row
                          key={virtualRow.key}
                          $isEven={virtualRow.index % 2 === 0}
                          style={{
                            position: 'absolute',
                            // `top`, not `transform`: a transformed ancestor breaks
                            // `position: sticky` on descendants (the frozen-column cells
                            // below), so virtualization must offset rows this way.
                            top: `${virtualRow.start}px`,
                            left: 0,
                            right: 0,
                            minWidth: tableMinWidth,
                            height: `${virtualRow.size}px`,
                          }}
                        >
                          <Cell
                            $minWidth={ROW_NUMBER_COLUMN_MIN_WIDTH}
                            $noGrow
                            $stickyLeft={0}
                            style={{
                              fontVariantNumeric: 'tabular-nums',
                              color: 'rgba(0, 0, 0, 0.55)',
                            }}
                          >
                            {virtualRow.index + 1}
                          </Cell>
                          <OverflowTooltip title={id}>
                            <Cell
                              $minWidth={EMPLOYEE_ID_COLUMN_MIN_WIDTH}
                              $noGrow
                              $stickyLeft={EMPLOYEE_ID_STICKY_LEFT}
                              style={
                                showMeasureColumn
                                  ? undefined
                                  : { borderRight: STICKY_BOUNDARY_BORDER }
                              }
                            >
                              {id}
                            </Cell>
                          </OverflowTooltip>
                          {showMeasureColumn ? (
                            <OverflowTooltip
                              title={
                                metricValueByEmployeeId[id] != null
                                  ? formatMetricForDrillDown(
                                      metricValueByEmployeeId[id]
                                    )
                                  : undefined
                              }
                            >
                              <ValueBodyCell
                                $minWidth={VALUE_COLUMN_MIN_WIDTH}
                                $noGrow
                                $stickyLeft={VALUE_STICKY_LEFT}
                                style={{ borderRight: STICKY_BOUNDARY_BORDER }}
                              >
                                {metricValueByEmployeeId[id] != null
                                  ? formatMetricForDrillDown(
                                      metricValueByEmployeeId[id]
                                    )
                                  : '—'}
                              </ValueBodyCell>
                            </OverflowTooltip>
                          ) : null}
                          {columns.map((column) => {
                            const key = getKeyFromDataFieldWithDataType(
                              column.field
                            );
                            const { minWidth } = getColumnLayout(column.field);
                            if (!column.accessible) {
                              // Never fetched (see accessibleFields), so there is no value
                              // here to hide — the cell states why it is empty.
                              return (
                                <Cell
                                  key={key}
                                  $minWidth={minWidth}
                                  style={{ fontStyle: 'italic', opacity: 0.6 }}
                                >
                                  {t(CONFIDENTIAL_DISPLAY_KEY)}
                                </Cell>
                              );
                            }
                            const value = entry?.[key] ?? '';
                            const isPending =
                              entry === undefined && !isRowLoading;
                            return (
                              <Cell key={key} $minWidth={minWidth}>
                                <CellLoaderWrap>
                                  {isRowLoading && (
                                    <CircularProgress size={14} />
                                  )}
                                  {isPending ? (
                                    '…'
                                  ) : value ? (
                                    // Ref goes on CellText, not the outer Cell — CellText is
                                    // the element that actually truncates, so it is the one
                                    // whose scrollWidth vs clientWidth tells us to show the
                                    // tooltip. CellLoaderWrap and Cell both shrink around it
                                    // without ever overflowing themselves.
                                    <OverflowTooltip title={value}>
                                      <CellText>{value}</CellText>
                                    </OverflowTooltip>
                                  ) : (
                                    <EmptyValue>—</EmptyValue>
                                  )}
                                </CellLoaderWrap>
                              </Cell>
                            );
                          })}
                        </Row>
                      );
                    })}
                  </div>
                </TableScroll>
                <Footer>
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
                  >
                    {hasMore
                      ? t(
                          'pages:dashboards.peopleBalanceSheet.cellDrillDown.employeesLoaded',
                          {
                            defaultValue:
                              '{{loaded}} of {{total}} employees loaded',
                            loaded: paginatedIds.length.toLocaleString(),
                            total: sortedEmployeeIds.length.toLocaleString(),
                          }
                        )
                      : `${sortedEmployeeIds.length.toLocaleString()} ${t(
                          'pages:dashboards.peopleBalanceSheet.cellDrillDown.employeesCount',
                          { defaultValue: 'employees' }
                        )}`}
                  </Typography>
                  <div
                    style={{ display: 'flex', gap: 8, alignItems: 'center' }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleLoadMore}
                      disabled={!hasMore}
                    >
                      {hasMore
                        ? t(
                            'pages:dashboards.peopleBalanceSheet.cellDrillDown.loadMore',
                            {
                              defaultValue: 'Load more ({{remaining}})',
                              remaining: (
                                sortedEmployeeIds.length - paginatedIds.length
                              ).toLocaleString(),
                            }
                          )
                        : t(
                            'pages:dashboards.peopleBalanceSheet.cellDrillDown.allLoaded',
                            { defaultValue: 'All loaded' }
                          )}
                    </Button>
                    {canExport && (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={handleExport}
                        disabled={isExporting}
                      >
                        {isExporting && exportProgress
                          ? t(
                              'pages:dashboards.peopleBalanceSheet.cellDrillDown.exportProgress',
                              {
                                defaultValue:
                                  'Preparing export… {{done}} / {{total}}',
                                done: exportProgress.done,
                                total: exportProgress.total,
                              }
                            )
                          : t(
                              'pages:dashboards.peopleBalanceSheet.cellDrillDown.export',
                              { defaultValue: 'Export CSV' }
                            )}
                      </Button>
                    )}
                  </div>
                </Footer>
              </TableContainer>
            )}
          </>
        )}
      </ModalContentWrap>
    </Modal>
  );
};
